import React, { useState, useEffect } from 'react';
import { guestsApi, reservationsApi, proofsApi, hotelsApi, paymentsApi } from '../../services/api';
import { fmt, fmtDate } from '../../utils/billing';
import { useNotify } from '../../context/ConfirmContext';

// ─── Privacy helpers ────────────────────────────────────────────────────────
// Mask name: show first 2 chars of each word, rest as *
function maskName(name) {
  if (!name) return '—';
  return name
    .split(' ')
    .map(w => w.length <= 2 ? w : w.slice(0, 2) + '*'.repeat(w.length - 2))
    .join(' ');
}

// Mask phone: show first 2 and last 2 digits, rest as *
function maskPhone(phone) {
  if (!phone) return '—';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 5) return '****';
  return digits.slice(0, 2) + '*'.repeat(digits.length - 4) + digits.slice(-2);
}

// Mask email: show first char + domain only
function maskEmail(email) {
  if (!email) return '—';
  const parts = email.split('@');
  if (parts.length < 2) return email[0] + '***';
  return email[0] + '***@' + parts[1];
}

// Detect if the search string looks like a phone number (mostly digits)
function isPhoneSearch(q) {
  if (!q) return false;
  const stripped = q.replace(/[\s\-+().]/g, '');
  return stripped.length >= 4 && /^\d+$/.test(stripped);
}
// ────────────────────────────────────────────────────────────────────────────

export default function GuestDirectory({ hotel }) {
  const notify = useNotify();
  const [guests, setGuests] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [guestRes, setGuestRes] = useState([]);
  const [guestProofs, setGuestProofs] = useState([]);
  const [filter, setFilter] = useState('all'); // all | individual | corporate
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [guestIdsInRange, setGuestIdsInRange] = useState(null);
  const [viewingFolio, setViewingFolio] = useState(null);
  const [folioPayments, setFolioPayments] = useState([]);

  // Is the current search unlocking full details?
  const unlocked = isPhoneSearch(search);

  useEffect(() => { loadGuests(); }, []);

  useEffect(() => {
    async function filterByDate() {
      if (!dateRange.start && !dateRange.end) {
        setGuestIdsInRange(null);
        return;
      }
      try {
        const reservations = await reservationsApi.list(hotel.id);
        let res = [];
        if (dateRange.start && dateRange.end) {
          res = reservations.filter(r => r.check_in >= dateRange.start && r.check_in <= dateRange.end);
        } else if (dateRange.start) {
          res = reservations.filter(r => r.check_in >= dateRange.start);
        } else {
          res = reservations.filter(r => r.check_in <= dateRange.end);
        }
        const ids = new Set(res.map(r => r.guest_id || r.guestId));
        setGuestIdsInRange(ids);
      } catch (err) {
        console.error('Date filter error:', err);
      }
    }
    filterByDate();
    // hotel?.id genuinely belongs here (not just satisfying the linter) — without
    // it, switching hotels while a date filter is active kept showing guest ids
    // filtered against the PREVIOUS hotel's reservations until dateRange changed.
  }, [dateRange, hotel?.id]);

  async function loadGuests() {
    try {
      const all = await guestsApi.list();
      const mapped = all.map(g => ({
        ...g,
        customerType: g.customer_type,
        idType: g.id_type,
        idNumber: g.id_number,
        companyGst: g.company_gst,
        createdAt: g.created_at
      }));
      setGuests(mapped);
    } catch (err) { console.error(err); }
  }

  async function selectGuest(g) {
    setSelected(g);
    try {
      const [res, proofs, allHotels] = await Promise.all([
        reservationsApi.list('', '', g.id),
        proofsApi.list(g.id),
        hotelsApi.list()
      ]);
      const hotelMap = {};
      allHotels.forEach(h => { hotelMap[h.id] = h; });
      setGuestProofs(proofs);
      setGuestRes(res.map(r => ({
        ...r,
        hotelName: hotelMap[r.hotel_id]?.name,
        checkIn: fmtDate(r.check_in),
        checkInTime: r.check_in_time,
        checkOut: fmtDate(r.check_out),
        checkOutTime: r.check_out_time,
        invoiceNumber: r.invoice_number
      })));
    } catch (err) { console.error('Select Error:', err); }
  }

  const filtered = guests.filter(g => {
    if (filter !== 'all' && g.customerType !== filter) return false;
    if (guestIdsInRange !== null && !guestIdsInRange.has(g.id)) return false;
    if (!search) return true;
    return g.name?.toLowerCase().includes(search.toLowerCase()) ||
      g.phone?.includes(search) || g.email?.toLowerCase().includes(search.toLowerCase()) ||
      g.idNumber?.includes(search) || g.company?.toLowerCase().includes(search.toLowerCase()) ||
      g.address?.toLowerCase().includes(search.toLowerCase()) ||
      g.nationality?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div>
      {/* Privacy notice */}
      <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 10, fontSize: 12, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8 }}>
        🔒 <span>Guest names and phone numbers are <strong>masked for privacy</strong>. Search by <strong>phone number</strong> to reveal full details.</span>
      </div>

      <div style={{ marginBottom: 12, padding: '10px 14px', background: 'rgba(126,200,200,0.08)', border: '1px solid rgba(126,200,200,0.2)', borderRadius: 10, fontSize: 12, color: 'var(--teal)' }}>
        🌐 Guest directory is <strong>shared across all properties</strong>. Guest profiles sync when a guest checks in at any hotel.
      </div>

      <div className="grid-2" style={{ alignItems: 'start' }}>
        {/* GUEST LIST */}
        <div className="panel" style={{ maxHeight: '78vh', overflowY: 'auto' }}>
          <div className="panel-head" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="panel-title">All Guests ({filtered.length})</span>
              {unlocked && (
                <span style={{ fontSize: 11, color: 'var(--success)', fontWeight: 700, background: 'rgba(34,197,94,0.1)', padding: '2px 8px', borderRadius: 20 }}>
                  🔓 Full details visible
                </span>
              )}
            </div>

            <div style={{ position: 'relative' }}>
              <input
                style={{ width: '100%', background: 'var(--bg)', border: `1px solid ${unlocked ? 'var(--success)' : 'var(--border2)'}`, borderRadius: 7, padding: '8px 12px 8px 34px', color: 'var(--text)', fontFamily: 'var(--font-b)', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
                placeholder="🔍 Type phone number to reveal details..."
                value={search}
                onChange={e => { setSearch(e.target.value); setSelected(null); }}
              />
              {search && (
                <button
                  onClick={() => { setSearch(''); setSelected(null); }}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 14 }}
                >✕</button>
              )}
            </div>

            {!unlocked && search && (
              <div style={{ fontSize: 11, color: 'var(--muted)', padding: '4px 2px' }}>
                ⚠️ Browsing by name only — details are masked. Enter a phone number to unlock.
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'end' }}>
              <div className="field">
                <label style={{ fontSize: 10, fontWeight: 700, opacity: 0.6, display: 'block', marginBottom: 2 }}>Check-in From</label>
                <input type="date" value={dateRange.start} onChange={e => setDateRange({ ...dateRange, start: e.target.value })}
                  style={{ width: '100%', padding: '6px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg)', color: 'var(--text)' }} />
              </div>
              <div className="field">
                <label style={{ fontSize: 10, fontWeight: 700, opacity: 0.6, display: 'block', marginBottom: 2 }}>Check-in To</label>
                <input type="date" value={dateRange.end} onChange={e => setDateRange({ ...dateRange, end: e.target.value })}
                  style={{ width: '100%', padding: '6px', fontSize: 11, border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg)', color: 'var(--text)' }} />
              </div>
              {(dateRange.start || dateRange.end) && (
                <button className="btn btn-sm btn-ghost" onClick={() => setDateRange({ start: '', end: '' })}
                  style={{ height: 28, color: 'var(--danger)', border: 'none', background: 'transparent' }}>✕</button>
              )}
            </div>

            <div style={{ display: 'flex', gap: 6 }}>
              {['all', 'individual', 'corporate'].map(f => (
                <button key={f} className={`btn btn-sm ${filter === f ? 'btn-gold' : 'btn-ghost'}`} onClick={() => setFilter(f)}>
                  {f === 'all' ? 'All' : f === 'individual' ? 'Individual' : 'Corporate'}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="empty"><div className="empty-icon">👤</div>No guests found</div>
          ) : filtered.map(g => (
            <div key={g.id} onClick={() => selectGuest(g)} style={{
              padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid var(--border)',
              background: selected?.id === g.id ? 'var(--s2)' : 'transparent',
              borderLeft: selected?.id === g.id ? '3px solid var(--accent)' : '3px solid transparent',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, fontFamily: unlocked ? 'inherit' : 'monospace', letterSpacing: unlocked ? 'normal' : '0.5px' }}>
                    {unlocked ? g.name : maskName(g.name)}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                    {unlocked ? g.phone : maskPhone(g.phone)}
                    {g.email && <> · {unlocked ? g.email : maskEmail(g.email)}</>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                    {g.idType}: {unlocked ? g.idNumber : '••••••••'}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span className={`badge ${g.customerType === 'corporate' ? 'b-amber' : 'b-blue'}`}>
                    {g.customerType === 'corporate' ? 'Corporate' : 'Individual'}
                  </span>
                  {g.customerType === 'corporate' && <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>{g.company}</div>}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* GUEST DETAIL */}
        <div>
          {!selected ? (
            <div className="panel">
              <div className="empty" style={{ padding: '60px 20px' }}>
                <div className="empty-icon">{unlocked ? '👆' : '🔒'}</div>
                {unlocked ? 'Select a guest to view details' : 'Search by phone number to view guest details'}
              </div>
            </div>
          ) : (
            <>
              <div className="panel" style={{ marginBottom: 14 }}>
                <div className="panel-head">
                  <span className="panel-title" style={{ fontFamily: 'var(--font-d)', fontSize: 18 }}>
                    {unlocked ? selected.name : maskName(selected.name)}
                  </span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span className={`badge ${selected.customerType === 'corporate' ? 'b-amber' : 'b-blue'}`}>
                      {selected.customerType === 'corporate' ? '🏢 Corporate' : '👤 Individual'}
                    </span>
                    {!unlocked && (
                      <span style={{ fontSize: 11, color: 'var(--muted)', background: 'rgba(239,68,68,0.08)', padding: '2px 8px', borderRadius: 12, border: '1px solid rgba(239,68,68,0.2)' }}>
                        🔒 Masked
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ padding: '16px 18px' }}>
                  {!unlocked && (
                    <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,0.06)', borderRadius: 8, marginBottom: 14, fontSize: 12, color: '#dc2626', border: '1px dashed rgba(239,68,68,0.25)' }}>
                      🔒 Full details are hidden. Search with this guest's <strong>phone number</strong> to reveal.
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 20px', fontSize: 12 }}>
                    {[
                      ['Phone', unlocked ? selected.phone : maskPhone(selected.phone)],
                      ['Email', unlocked ? selected.email : maskEmail(selected.email)],
                      ['Nationality', selected.nationality],
                      [selected.idType, unlocked ? selected.idNumber : '••••••••'],
                      ['Address', unlocked ? selected.address : (selected.address ? '•'.repeat(12) : null)],
                    ].map(([k, v]) => v && (
                      <div key={k} style={{ gridColumn: k === 'Address' ? '1/-1' : undefined }}>
                        <span style={{ color: 'var(--muted)' }}>{k}: </span>
                        <span style={{ fontWeight: 600, fontFamily: (!unlocked && ['Phone', selected.idType, 'Address'].includes(k)) ? 'monospace' : 'inherit' }}>{v}</span>
                      </div>
                    ))}
                    {selected.customerType === 'corporate' && <>
                      <div style={{ gridColumn: '1/-1', marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                        <span style={{ color: 'var(--muted)' }}>Company: </span><span style={{ fontWeight: 600 }}>{selected.company}</span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--muted)' }}>Company GST: </span>
                        <span style={{ fontWeight: 600, color: 'var(--teal)' }}>{unlocked ? selected.companyGst : '••••••••••'}</span>
                      </div>
                    </>}
                  </div>
                </div>
              </div>

              {/* Stay history */}
              <div className="panel" style={{ marginBottom: 14 }}>
                <div className="panel-head"><span className="panel-title">Stay History</span><span className="panel-sub">{guestRes.length} bookings</span></div>
                {guestRes.length === 0 ? <div className="empty">No stays yet</div> : (
                  <div className="tbl-wrap">
                    <table>
                      <thead><tr><th>Hotel</th><th>Room</th><th>Check-in</th><th>Check-out</th><th>Invoice</th><th>Action</th></tr></thead>
                      <tbody>
                        {guestRes.map(r => (
                          <tr key={r.id}>
                            <td>{r.hotelName}</td>
                            <td>{r.room}<div className="td-sub">{r.roomType}</div></td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{r.checkIn}</div>
                              <div style={{ fontSize: 10, color: 'var(--muted)' }}>{r.checkInTime || '—'}</div>
                            </td>
                            <td>
                              <div style={{ fontWeight: 600 }}>{r.checkOut}</div>
                              <div style={{ fontSize: 10, color: 'var(--muted)' }}>{r.checkOutTime || '—'}</div>
                            </td>
                            <td>
                              {r.invoiceNumber ? (
                                <span style={{ fontWeight: 700, color: 'var(--teal)', fontSize: 11 }}>{r.invoiceNumber}</span>
                              ) : '—'}
                            </td>
                            <td>
                              {unlocked ? (
                                <button
                                  className="btn btn-sm btn-teal"
                                  style={{ fontSize: 10, padding: '4px 8px' }}
                                  onClick={async () => {
                                    try {
                                      const payments = await paymentsApi.forReservation(r.id);
                                      setFolioPayments(payments);
                                      setViewingFolio(r);
                                    } catch (err) { await notify(err.message); }
                                  }}
                                >
                                  View Folio
                                </button>
                              ) : (
                                <span style={{ fontSize: 10, color: 'var(--muted)' }}>🔒 Locked</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* ID Proofs */}
              <div className="panel">
                <div className="panel-head"><span className="panel-title">ID Proof Documents</span></div>
                <div style={{ padding: '14px 16px' }}>
                  {!unlocked ? (
                    <div style={{ fontSize: 12, color: 'var(--muted)', padding: '10px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                      🔒 Search by phone number to view ID documents.
                    </div>
                  ) : guestProofs.length === 0 ? (
                    <div style={{ color: 'var(--muted)', fontSize: 12 }}>No documents uploaded</div>
                  ) : (
                    <div className="proof-preview">
                      {guestProofs.map((p, i) => (
                        <div key={i} className="proof-thumb">
                          {p.data?.startsWith('data:image') ? <img src={p.data} alt={p.fileName} /> : <span>📄</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* FOLIO MODAL — only accessible when unlocked */}
      {viewingFolio && unlocked && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 600, padding: 0, borderRadius: 12, overflow: 'hidden' }}>
            <div style={{ background: 'var(--s1)', padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid var(--border)' }}>
              <span style={{ fontWeight: 700 }}>Stay Folio — {viewingFolio.invoiceNumber}</span>
              <button className="btn btn-ghost" onClick={() => setViewingFolio(null)}>✕</button>
            </div>
            <div style={{ padding: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase' }}>Stay Detail</div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginTop: 4 }}>{selected.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)' }}>Room {viewingFolio.room} · {viewingFolio.roomType}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase' }}>Dates</div>
                  <div style={{ fontWeight: 600, fontSize: 13, marginTop: 4 }}>{viewingFolio.checkIn} — {viewingFolio.checkOut}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{viewingFolio.nights} nights</div>
                </div>
              </div>

              <div style={{ background: 'var(--bg)', borderRadius: 8, border: '1px solid var(--border)', overflow: 'hidden', marginBottom: 20 }}>
                <table className="tbl-inv" style={{ fontSize: 12 }}>
                  <thead style={{ background: 'var(--s1)' }}>
                    <tr><th>Description</th><th style={{ textAlign: 'right' }}>Amount</th></tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Room Charges ({fmt(viewingFolio.room_rate)} × {viewingFolio.nights})</td>
                      <td style={{ textAlign: 'right' }}>{fmt(Number(viewingFolio.room_rate) * Number(viewingFolio.nights))}</td>
                    </tr>
                    {Number(viewingFolio.extra_charges) > 0 && (
                      <tr><td>Extra Charges</td><td style={{ textAlign: 'right' }}>{fmt(viewingFolio.extra_charges)}</td></tr>
                    )}
                    {Number(viewingFolio.damage_charges) > 0 && (
                      <tr><td>Damage Charges</td><td style={{ textAlign: 'right' }}>{fmt(viewingFolio.damage_charges)}</td></tr>
                    )}
                    {viewingFolio.billing_type === 'gst' && (
                      <tr>
                        <td>GST ({viewingFolio.gst_rate}%)</td>
                        <td style={{ textAlign: 'right' }}>{fmt(viewingFolio.gst_amount)}</td>
                      </tr>
                    )}
                    <tr style={{ background: 'var(--s2)', fontWeight: 700 }}>
                      <td>TOTAL PAYABLE</td>
                      <td style={{ textAlign: 'right' }}>{fmt(viewingFolio.total_amount)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 8 }}>Payment History</div>
                {folioPayments.length === 0 ? (
                  <div style={{ fontSize: 12, color: 'var(--muted)', padding: 10 }}>No payments recorded.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 6 }}>
                    {folioPayments.map((p, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--s1)', borderRadius: 6, fontSize: 12 }}>
                        <div>
                          <span style={{ fontWeight: 600 }}>{p.type.toUpperCase()}</span>
                          <span style={{ color: 'var(--muted)', marginLeft: 8 }}>{fmtDate(p.created_at)}</span>
                        </div>
                        <div style={{ fontWeight: 700 }}>{fmt(p.amount)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
